import { ColorCountOptionDto } from '@/api-sdk';
import { LitElement, css, html, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { Ref, createRef, ref } from 'lit/directives/ref.js';
import TomSelect from 'tom-select';

import tomSelectMinified from '@/assets/css/tom-select.min.css?inline';
import { BREAKPOINT_MEDIUM_PX } from '@/settings';
import {
  baseStyles,
  formStyles,
  rootVariablesStyles,
  tomSelectStyles,
  typographyStyles,
} from '@/styles';

@customElement('fg-print-option-color-count')
export class FgPrintOptionColorCount extends LitElement {
  @property({ type: Array })
  colorCount?: ColorCountOptionDto[] | null;

  @property({ type: String })
  method?: string;

  @property({ type: Boolean })
  updateInput = false;

  @state()
  currentMethod: undefined;

  @state()
  currentColorCount?: number;

  @state()
  tomSelect?: TomSelect;

  selectColorRef?: Ref<HTMLSelectElement> = createRef();

  firstUpdated(): void {
    if (!this.selectColorRef?.value || !this.colorCount) {
      return;
    }

    const tomSelect = new TomSelect(this.selectColorRef.value, {
      controlInput:
        null as any /** This will prevent typing in the input, as any wil remove typing error */,
    });

    this.renderOptionsInSelect();
    this.tomSelect = tomSelect;
  }

  protected willUpdate(): void {
    this.renderOptionsInSelect(true);
  }

  renderOptionsInSelect(clearSelect?: boolean) {
    if (!this.colorCount) {
      return;
    }

    if (clearSelect) {
      this.tomSelect?.clear();
      this.tomSelect?.clearOptions();
    }

    this.tomSelect?.off('change');

    const options = this.colorCount?.map((color, index) => ({
      value: index + 1,
      text: color.name,
      selected: color.selected,
    }));

    const selectedIndex = Object.values(options).findIndex(
      option => option.selected,
    );

    if (options) {
      this.tomSelect?.addOptions(options);
      this.tomSelect?.addItem(options[selectedIndex].value.toString() || '');
    }

    this.tomSelect?.on('change', (value: string) => {
      if (value) {
        this.onPrintOptionChange(this, value);
      }
    });
  }

  /**
   * Handle the change event from the TomSelect library. Here we will check what print method is slecected and dispatch
   * the event to the configurator component to update the print option.
   *
   * @param self {FgPrintOptionColorCount} - To prevent the this context from the TomSelect library, and used the LitElement context
   * @param event {string}
   */
  onPrintOptionChange(self: FgPrintOptionColorCount, event: string) {
    if (!self.colorCount) {
      return;
    }

    const value = event;

    if (value === '') {
      self.dispatchEvent(new CustomEvent('no-print-selected'));
      return;
    }

    const correctIndex = parseInt(value) - 1;
    const { queryString } = self.colorCount[correctIndex];

    self.onOptionSelected(queryString);
  }

  /**
   * Dispatch event to configurator component to update the print option. The configurator will handle the call to the API.
   * @param queryString {string}
   */
  onOptionSelected(queryString: string) {
    this.dispatchEvent(
      new CustomEvent('option-selected', {
        composed: true,
        detail: {
          option: {
            queryString: queryString,
          },
        },
      }),
    );
  }

  render() {
    const selectReference = ref(this.selectColorRef);
    return html`
      <select
        @change=${(event: { target: HTMLSelectElement }) => {
          if (event.target.value) {
            this.onPrintOptionChange(this, event.target.value);
          }
        }}
      >
        ${this.colorCount?.map(
          (color, index) => html`
            <option value="${index + 1}" ?selected="${color.selected}">
              ${color.name}
            </option>
          `,
        )}
      </select>
      <select ${selectReference}></select>
    `;
  }

  static styles = [
    unsafeCSS(tomSelectMinified),
    baseStyles,
    formStyles,
    rootVariablesStyles,
    tomSelectStyles,
    typographyStyles,
    css`
      .ts-wrapper .option {
        font-weight: 600;
        display: flex;
        align-items: center;
        min-height: 42px;
        padding: 8px 12px;
        border-radius: var(
          --fg-borderradius--xs,
          var(--fg-fallback-borderradius--xs)
        );
      }

      .ts-wrapper .option.selected {
        border: 1px var(--fg-success, var(--fg-fallback-success)) solid;
      }

      select {
        display: block;
        width: 100%;
      }

      .ts-wrapper {
        display: none;
      }

      @media screen and (min-width: ${BREAKPOINT_MEDIUM_PX}px) {
        select {
          display: none;
        }
        .ts-wrapper {
          display: block;
        }
      }
    `,
  ];
}
